import { createSlice } from "@reduxjs/toolkit";

const initialLookupState = { data: [], loading: false, error: null };

const initialState = {
  // --- LOOKUPS (Metadata) ---
  lookups: {
    countries: { ...initialLookupState },
    currencies: { ...initialLookupState },
    quantityUnits: { ...initialLookupState },
    packageUnits: { ...initialLookupState },
    taxGroups: { ...initialLookupState },
    paymentModes: { ...initialLookupState },
    salesStatus: { ...initialLookupState },
    stockIoTypes: { ...initialLookupState },
    transactionTypes: { ...initialLookupState },
    refundReasons: { ...initialLookupState },
    notices: { ...initialLookupState },
    branches: { ...initialLookupState },
  },

  // --- ITEM CLASS CODES (Paginated) ---
  loadingItemClassCodes: false,
  errorLoadingItemClassCodes: null,
  itemClassCodes: [],
  itemClassCodePagination: null,

  // --- FISCAL ITEMS (Paginated) ---
  loadingFiscalItems: false,
  errorLoadingFiscalItems: null,
  fiscalItems: [],
  fiscalItemPagination: null,

  loadingFiscalItemCreate: false,
  errorCreatingFiscalItem: null,
  loadingFiscalItemUpdate: false,
  errorUpdatingFiscalItem: null,
  loadingStockAdjust: false,
  errorStockAdjust: null,

  // --- FISCAL INVOICES (Paginated) ---
  loadingFiscalInvoices: false,
  errorLoadingFiscalInvoices: null,
  fiscalInvoices: [],
  fiscalInvoicePagination: null,

  loadingFiscalInvoiceCreate: false,
  errorCreatingFiscalInvoice: null,

  loadingFiscalInvoice: false,
  errorLoadingFiscalInvoice: null,
  currentFiscalInvoice: null,
  pdfUrl: null,

  loadingInvoiceEmail: false,
  errorInvoiceEmail: null,

  serverStatus: null,
};

export const fiscalSlice = createSlice({
  name: "fiscal",
  initialState,
  reducers: {
    // --- GENERIC LOOKUPS ---
    setLookupLoading(state, { payload: key }) {
      state.lookups[key].loading = true;
      state.lookups[key].error = null;
    },
    setLookupError(state, { payload: { key, error } }) {
      state.lookups[key].loading = false;
      state.lookups[key].error = error;
    },
    setLookupData(state, { payload: { key, data } }) {
      state.lookups[key].loading = false;
      state.lookups[key].error = null;
      state.lookups[key].data = data;
    },

    // --- ITEM CLASS CODES ---
    setLoadingItemClassCodes(state) {
      state.loadingItemClassCodes = true;
      state.errorLoadingItemClassCodes = null;
    },
    setErrorItemClassCodes(state, { payload }) {
      state.loadingItemClassCodes = false;
      state.errorLoadingItemClassCodes = payload;
    },
    setItemClassCodes(state, { payload: { content, pagination } }) {
      state.loadingItemClassCodes = false;
      state.errorLoadingItemClassCodes = null;
      state.itemClassCodes = content;
      state.itemClassCodePagination = pagination;
    },

    // --- FISCAL ITEMS ---
    setLoadingFiscalItems(state) {
      state.loadingFiscalItems = true;
      state.errorLoadingFiscalItems = null;
    },
    setErrorFiscalItems(state, { payload }) {
      state.loadingFiscalItems = false;
      state.errorLoadingFiscalItems = payload;
    },
    setFiscalItems(state, { payload: { content, pagination } }) {
      state.loadingFiscalItems = false;
      state.errorLoadingFiscalItems = null;
      state.fiscalItems = content;
      state.fiscalItemPagination = pagination;
    },

    // Fiscal Item Operations
    setLoadingFiscalItemCreate(state) {
      state.loadingFiscalItemCreate = true;
      state.errorCreatingFiscalItem = null;
    },
    setErrorFiscalItemCreate(state, { payload }) {
      state.loadingFiscalItemCreate = false;
      state.errorCreatingFiscalItem = payload;
    },
    setLoadingFiscalItemUpdate(state) {
      state.loadingFiscalItemUpdate = true;
      state.errorUpdatingFiscalItem = null;
    },
    setErrorFiscalItemUpdate(state, { payload }) {
      state.loadingFiscalItemUpdate = false;
      state.errorUpdatingFiscalItem = payload;
    },
    setLoadingStockAdjust(state) {
      state.loadingStockAdjust = true;
      state.errorStockAdjust = null;
    },
    setErrorStockAdjust(state, { payload }) {
      state.loadingStockAdjust = false;
      state.errorStockAdjust = payload;
    },

    // --- FISCAL INVOICES ---
    setLoadingFiscalInvoices(state) {
      state.loadingFiscalInvoices = true;
      state.errorLoadingFiscalInvoices = null;
    },
    setErrorFiscalInvoices(state, { payload }) {
      state.loadingFiscalInvoices = false;
      state.errorLoadingFiscalInvoices = payload;
    },
    setFiscalInvoices(state, { payload: { content, pagination } }) {
      state.loadingFiscalInvoices = false;
      state.errorLoadingFiscalInvoices = null;
      state.fiscalInvoices = content;
      state.fiscalInvoicePagination = pagination;
    },

    // Fiscal Invoice Operations
    setLoadingFiscalInvoiceCreate(state) {
      state.loadingFiscalInvoiceCreate = true;
      state.errorCreatingFiscalInvoice = null;
    },
    setErrorFiscalInvoiceCreate(state, { payload }) {
      state.loadingFiscalInvoiceCreate = false;
      state.errorCreatingFiscalInvoice = payload;
    },
    setLoadingFiscalInvoice(state) {
      state.loadingFiscalInvoice = true;
      state.errorLoadingFiscalInvoice = null;
    },
    setErrorLoadingFiscalInvoice(state, { payload }) {
      state.loadingFiscalInvoice = false;
      state.errorLoadingFiscalInvoice = payload;
    },
    setCurrentFiscalInvoice(state, { payload }) {
      state.loadingFiscalInvoice = false;
      state.errorLoadingFiscalInvoice = null;
      state.currentFiscalInvoice = payload;
    },
    createFiscalInvoiceSuccess(state, { payload }) {
      state.loadingFiscalInvoiceCreate = false;
      state.errorCreatingFiscalInvoice = null;
      state.fiscalInvoices = [payload, ...state.fiscalInvoices];
    },
    setEmailSuccess: (state) => {
      state.loadingInvoiceEmail = false;
    },
    setPdfUrl(state, { payload }) {
      state.loadingInvoicePdf = false;
      state.pdfUrl = payload;
    },
    clearPdfUrl(state) {
      if (state.pdfUrl) URL.revokeObjectURL(state.pdfUrl);
      state.pdfUrl = null;
    },
    setLoadingInvoicePdf(state) {
      state.loadingInvoicePdf = true;
      state.errorInvoicePdf = null;
    },
    setErrorInvoicePdf(state, { payload }) {
      state.loadingInvoicePdf = false;
      state.errorInvoicePdf = payload;
    },
    setLoadingInvoiceEmail(state) {
      state.loadingInvoiceEmail = true;
      state.errorInvoiceEmail = null;
    },
    setErrorInvoiceEmail(state, { payload }) {
      state.loadingInvoiceEmail = false;
      state.errorInvoiceEmail = payload;
    },

    setFiscalServerResponseStatus(state, { payload }) {
      state.serverStatus = payload;
    },
  },
});

export const {
  setLookupLoading,
  setLookupError,
  setLookupData,

  setLoadingItemClassCodes,
  setErrorItemClassCodes,
  setItemClassCodes,

  setLoadingFiscalItems,
  setErrorFiscalItems,
  setFiscalItems,

  setLoadingFiscalItemCreate,
  setErrorFiscalItemCreate,
  setLoadingFiscalItemUpdate,
  setErrorFiscalItemUpdate,
  setLoadingStockAdjust,
  setErrorStockAdjust,

  setLoadingFiscalInvoices,
  setErrorFiscalInvoices,
  setFiscalInvoices,

  setLoadingFiscalInvoiceCreate,
  setErrorFiscalInvoiceCreate,
  setCurrentFiscalInvoice,
  setErrorInvoicePdf,
  setErrorLoadingFiscalInvoice,
  setLoadingFiscalInvoice,
  setLoadingInvoicePdf,
  setEmailSuccess,
  setPdfUrl,
  clearPdfUrl,
  createFiscalInvoiceSuccess,
  setLoadingInvoiceEmail,
  setErrorInvoiceEmail,

  setFiscalServerResponseStatus,
} = fiscalSlice.actions;

export default fiscalSlice.reducer;
